import { AddUserResponse, UserResponse, UserRole } from 'compliance-api/generated';
import { ReactNode, useCallback, useId, useState } from 'react';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { AddUserModal } from '@/modules/admin-dashboard/components/modals/AddUserModal.tsx';
import { useUsersListQuery } from '@/modules/admin-dashboard/queries/users.ts';
import { Avatar, SelectPaginate, SelectPaginateOption } from 'ui';
import { PlusBlueIcon } from 'ui/icons';

type baseProps = {
  label?: string;
  placeholder?: string;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  isClearable?: boolean;
  enableCreation?: boolean;
  enableCreationLabel?: string;
  enableAddedEmail?: boolean;
  user_role?: UserRole;
  formatCreateLabel?: string;
  fixedOption?: SelectPaginateOption<number>;
  excludeUserIds?: number[];
};

type singleProps = {
  isMulti: false;
  onChange: (options: SelectPaginateOption<number> | null, title?: string) => void;
  defaultValue?: SelectPaginateOption<number>;
  value?: SelectPaginateOption<number> | null;
};

type multiProps = {
  isMulti: true;
  onChange: (options: SelectPaginateOption<number>[], title?: string) => void;
  defaultValue?: SelectPaginateOption<number>[];
  value?: SelectPaginateOption<number>[];
};

type UsersSelectPaginatedProps = baseProps & (singleProps | multiProps);
type UserSelectBase = Pick<UserResponse, 'id' | 'first_name' | 'last_name' | 'avatar_url'>;

export const selectUserOption = (user: UserSelectBase) => ({
  label: (user.first_name + ' ' + user.last_name) as ReactNode,
  value: user.id,
  avatar: (
    <Avatar
      withBorder
      size="sm"
      className="border-[#C4C4C4]"
      classNameLetters="text-[12px]"
      firstName={user.first_name}
      lastName={user.last_name}
      src={user?.avatar_url}
    />
  ),
  avatar_url: user?.avatar_url,
  first_name: user?.first_name,
  last_name: user?.last_name,
});

export const mapUsers = (users: UserSelectBase[]) => users.map(user => selectUserOption(user));

export const UsersSelectPaginated = ({
  label,
  value,
  error,
  isMulti,
  onChange,
  className,
  isClearable,
  defaultValue,
  placeholder,
  formFieldTitle,
  enableCreation,
  disabled = false,
  enableAddedEmail = false,
  user_role,
  formatCreateLabel,
  fixedOption,
  excludeUserIds = [],
  enableCreationLabel = 'Add user',
}: UsersSelectPaginatedProps) => {
  const businessId = useGetCurrentBusinessId();
  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [cacheUniq, setCacheUniq] = useState(Math.random());

  const { fetchNextPage } = useUsersListQuery(
    {
      page: 1,
      page_size: 15,
      filter_by_name: '',
      user_role,
    },
    { disabled: true, cacheKey: useId() },
  );

  const handleFetchNextUsers = useCallback(
    async (page: number, search?: string) => {
      const usersData = await fetchNextPage({ pageParam: { page, search } });
      const users = usersData?.data?.pages?.at(-1)?.data || [];
      const filteredUsers = users.filter(user => !excludeUserIds.includes(user.id));

      return mapUsers(filteredUsers);
    },
    [excludeUserIds, fetchNextPage],
  );

  const handleChange = useCallback(
    (options: SelectPaginateOption<number> | null | SelectPaginateOption<number>[]) => {
      onChange(options as any, formFieldTitle);
    },
    [formFieldTitle, onChange],
  );

  const handleUserAdded = useCallback(
    (res: AddUserResponse) => {
      if (excludeUserIds.includes(res.user.id)) {
        return;
      }
      setCacheUniq(Math.random());
      if (isMulti) {
        onChange([...(value || []), ...mapUsers([res.user])], formFieldTitle);
      } else {
        onChange(mapUsers([res.user])[0], formFieldTitle);
      }
    },
    [excludeUserIds, formFieldTitle, isMulti, onChange, value],
  );

  const addedLastOption = enableCreation
    ? {
        label: (
          <div className="flex gap-2">
            <img src={PlusBlueIcon} alt="add" />
            <span>{enableCreationLabel}</span>
          </div>
        ),
        onClick: () => {
          setIsCreationOpen(true);
        },
      }
    : undefined;

  return (
    <>
      <SelectPaginate
        cacheUniqs={[cacheUniq, businessId]}
        isMulti={isMulti}
        error={error}
        value={enableCreation ? value || null : value}
        label={label}
        autoHeight={true}
        disabled={disabled}
        className={className}
        isClearable={isClearable}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onChange={handleChange}
        onAsync={handleFetchNextUsers}
        debounceTimeoutSearch={700}
        addedLastOption={addedLastOption}
        withCreatable={enableAddedEmail}
        formatCreateLabel={formatCreateLabel}
        fixedOption={fixedOption}
      />
      {isCreationOpen && (
        <AddUserModal
          open={isCreationOpen}
          onClose={() => setIsCreationOpen(false)}
          onSuccess={handleUserAdded}
        />
      )}
    </>
  );
};

export default UsersSelectPaginated;
